<!--
  Replays shell — two-way switch:
    1. `showInitialSkeleton` → full-page skeleton (nav / filter / refetch).
    2. Otherwise → real content.
-->
<!-- Profile panel takes over the page when opened from the header -->
@if (isProfileOpen) {
  <section class="profile-inline-panel tw-px-[28px] tw-pt-[16px]" data-cy="replays-profile">
    <!-- Favorites stay visible above the profile takeover -->
    <ng-container *ngTemplateOutlet="favoritesBar"></ng-container>
    <feature-mat-profile/>
  </section>
} @else if (showInitialSkeleton) {
  <ng-container *ngTemplateOutlet="refReplaysInitialSkeleton"></ng-container>
} @else {
  <ng-container *ngTemplateOutlet="refReplaysContent"></ng-container>
}

<ng-template #refReplaysContent>
<section
  class="replays-search tw-px-[28px] tw-pt-[16px]"
  data-cy="replays-screen"
>

  <ng-container *ngTemplateOutlet="favoritesBar"></ng-container>

  <!-- Search video replays pill -->
  <div
    class="
      tw-flex
    "
  >
    <div
      class="
        tw-relative
        tw-bottom-[-18px]
        tw-z-[-1]
        tw-capitalize
        tw-bg-[#769ED1]
        tw-text-center
        tw-text-[18px]
        tw-text-[#FFFFFF]
        tw-rounded-[4px]
        tw-pt-[5px]
        tw-pb-[12px]
        tw-px-[15px]
      "
    >
      {{ 'replaysearch.header' | translate: 'Search Video Replays' }}
    </div>
  </div>

  <!-- Results panel -->
  <div class="replays-panel tw-mt-[8px]">

    <!-- Results count -->
    <div
      class="results-count tw-px-[16px] tw-pt-[20px] tw-pb-[12px]"
      data-cy="replays-results-count"
    >
      <span class="tw-text-[#FF6F6E]">{{ (replays.length || 0) }}</span> {{ 'replaysearch.resultsfound' | translate: 'Results found' }}
    </div>

    <!-- Filter row: labelled fields (date / breed / track / runner / head-to-head) + Clear -->
    <div class="filter-row tw-flex tw-flex-wrap tw-items-end tw-gap-[16px] tw-px-[16px] tw-pb-[20px]">

      <!-- Date -->
      <div class="filter-field filter-field--date tw-flex tw-flex-col">
        <span class="filter-field__label">{{ 'replaysearch.bydate' | translate: 'By date' }}</span>
        <mat-form-field
          appearance="outline"
          class="date-input"
          subscriptSizing="dynamic"
        >
          <input
            matInput
            [matDatepicker]="picker"
            [max]="maxDate"
            [value]="formVal['dateBy'].value"
            (dateChange)="setDate($event)"
            placeholder="{{ 'ui.wager.mm.dd.yyyy' | translate: 'MM/DD/YYYY' }}"
            data-cy="replays-date-input"
          />
          <mat-datepicker-toggle matIconSuffix [for]="picker">
            <img
              matDatepickerToggleIcon
              src="/assets/images/wager/calendar_icon.svg"
              alt="{{ 'ui.wager.open.calendar' | translate: 'Open calendar' }}"
              class="date-input__toggle-icon"
            />
          </mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
      </div>

      <!-- Today shortcut — hidden per design; kept in DOM for future re-enable -->
      <button
        mat-stroked-button
        type="button"
        hidden
        class="today-btn !tw-hidden"
        (click)="todayDate()"
        data-cy="replays-today-btn"
        aria-hidden="true"
        tabindex="-1"
      >
        {{ 'Today' | translate: 'Today' }}
      </button>

      <!-- Breed -->
      <div class="filter-field filter-field--breed tw-flex tw-flex-col">
        <span class="filter-field__label">{{ 'generic.breed' | translate: 'Breed' }}</span>
        <mat-form-field
          appearance="outline"
          class="breed-input"
          subscriptSizing="dynamic"
        >
          <mat-select
            panelClass="replays-breed-panel"
            [value]="formVal['breed'].value"
            (selectionChange)="selectBreed($event)"
            disableOptionCentering
            data-cy="replays-breed-dropdown"
          >
            <mat-option
              *ngFor="let item of HORSE_BREED | keyvalue; trackBy: trackBreedFn"
              [value]="item.key"
            >
              {{ item.value }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Track (client-side filter) — enabled once results arrive -->
      @if (trackNames.length) {
        <div class="filter-field filter-field--track tw-flex tw-flex-col">
          <span class="filter-field__label">{{ 'ui.wager.tracks' | translate: 'Tracks' }}</span>
          <mat-form-field
            appearance="outline"
            class="track-input"
            subscriptSizing="dynamic"
          >
            <mat-select
              panelClass="replays-track-panel"
              [value]="track"
              (selectionChange)="selectTrack($event)"
              placeholder="{{ 'generic.track' | translate: 'Track' }}"
              disableOptionCentering
              data-cy="replays-track-dropdown"
            >
              <mat-option [value]="''">{{ 'ui.wager.all.tracks' | translate: 'All Tracks' }}</mat-option>
              <mat-option
                *ngFor="let name of trackNames; trackBy: trackTrackFn"
                [value]="name"
              >
                {{ name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      }

      <!-- By Runner Name -->
      <div class="filter-field filter-field--runner tw-flex tw-flex-col">
        <span class="filter-field__label">{{ 'replaysearch.byrunner' | translate: 'By Runner Name' }}</span>
        <mat-form-field
          appearance="outline"
          class="runner-name-input"
          subscriptSizing="dynamic"
        >
          <input
            matInput
            type="text"
            placeholder="{{ 'ui.wager.search.runner' | translate: 'Search runner…' }}"
            [value]="formVal['runnerName'].value"
            (keyup)="searchName('runner', $event)"
            [matAutocomplete]="runnerAuto"
            data-cy="replays-runner-input"
          />
          @if (isLoadingRunner) {
            <mat-progress-spinner
              matSuffix
              mode="indeterminate"
              diameter="18"
              class="runner-name-input__spinner"
            ></mat-progress-spinner>
          } @else if (formVal['runnerName'].value) {
            <button
              matSuffix
              mat-icon-button
              type="button"
              class="runner-name-input__clear"
              (click)="clearRunner('runner')"
              data-cy="replays-runner-clear"
              attr.aria-label="{{ 'ui.wager.clear.runner.name' | translate: 'Clear runner name' }}"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
          <mat-autocomplete
            #runnerAuto="matAutocomplete"
            (optionSelected)="selectName($event.option.value, 'runner')"
          >
            @for (name of runnersName; track name) {
              <mat-option [value]="name">{{ name }}</mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </div>

      <!-- Additional Runner Name (Head to Head) -->
      <div class="filter-field filter-field--head tw-flex tw-flex-col">
        <span class="filter-field__label">
          {{ 'ui.wager.additional.runner.name.head.to.head' | translate: 'Additional Runner Name (Head to Head)' }}
        </span>
        <mat-form-field
          appearance="outline"
          class="runner-name-input"
          subscriptSizing="dynamic"
        >
          <input
            matInput
            type="text"
            placeholder="{{ 'ui.wager.search.runner' | translate: 'Search runner…' }}"
            [value]="formVal['headToHead'].value"
            (keyup)="searchName('head', $event)"
            [matAutocomplete]="headAuto"
            data-cy="replays-head-input"
          />
          @if (isLoadingHead) {
            <mat-progress-spinner
              matSuffix
              mode="indeterminate"
              diameter="18"
              class="runner-name-input__spinner"
            ></mat-progress-spinner>
          } @else if (formVal['headToHead'].value) {
            <button
              matSuffix
              mat-icon-button
              type="button"
              class="runner-name-input__clear"
              (click)="clearRunner('head')"
              data-cy="replays-head-clear"
              attr.aria-label="{{ 'ui.wager.clear.additional.runner.name' | translate: 'Clear additional runner name' }}"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
          <mat-autocomplete
            #headAuto="matAutocomplete"
            (optionSelected)="selectName($event.option.value, 'head')"
          >
            @for (name of headToHeadName; track name) {
              <mat-option [value]="name">{{ name }}</mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </div>

      <!-- Clear — sits on the input baseline, aligned with the pills -->
      <button
        mat-flat-button
        type="button"
        class="clear-btn"
        (click)="clear()"
        data-cy="replays-clear-btn"
      >
        {{ 'generic.clear' | translate: 'Clear' }}
      </button>
    </div>

    <!-- Results table -->
    <div class="tw-overflow-auto tw-w-full tw-px-[15px] tw-pb-[10px]">
      <table
        class="replays-table tw-w-full"
        data-cy="replays-table"
      >
        <thead>
          <tr>
            <th>{{ 'generic.track' | translate: 'Track' }}</th>
            <th>{{ 'generic.date' | translate: 'Date' }}</th>
            <th>{{ 'generic.breed' | translate: 'Breed' }}</th>
            <th>{{ 'generic.race' | translate: 'Race' }}</th>
            <th>{{ 'STABLE_SYSTEM.DISTANCE' | translate: 'Distance' }}</th>
            <th>{{ 'STABLE_SYSTEM.SURFACE' | translate: 'Surface' }}</th>
            <th>{{ 'generic.purse' | translate: 'Purse' }}</th>
          </tr>
        </thead>
        <tbody>

          @if (isLoadingReplays) {
            <tr class="loading-row">
              <td colspan="7" class="tw-text-center tw-py-[24px]">
                <mat-progress-spinner
                  mode="indeterminate"
                  diameter="32"
                  class="tw-inline-block"
                ></mat-progress-spinner>
              </td>
            </tr>
          } @else if (!replays.length) {
            <tr class="empty-row">
              <td colspan="7" class="tw-text-center tw-py-[24px]">
                {{ 'ui.wager.no.replays.found.for.the.selected' | translate: 'No replays found for the selected filters.' }}
              </td>
            </tr>
          } @else {
            <tr
              *ngFor="let replay of replays; trackBy: trackReplayFn"
              (click)="getReplayVideo(replay)"
              class="tw-cursor-pointer replay-row"
              [attr.data-cy]="'replays-row-' + replay?.trackId + '-' + replay?.race"
            >
              <td>{{ replay?.track }}</td>
              <td>{{ replay?.date | dateFormat: 'MM-DD-YYYY' }}</td>
              <td>{{ replay?.breed }}</td>
              <td>{{ replay?.race }}</td>
              <td>{{ replay?.distance }}</td>
              <td>{{ replay?.surface }}</td>
              <td>${{ replay?.purse | numberWithCommas: 0 }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

</section>
</ng-template>

<!--
  Full-page skeleton — first fetch. Mirrors the real layout
  (favorites, tabs, filter row, table) with shimmering bars.
-->
<ng-template #refReplaysInitialSkeleton>
  <section
    class="replays-search replays-skeleton tw-px-[28px] tw-pt-[16px]"
    aria-hidden="true"
    data-cy="replays-loading-skeleton"
  >
    <!-- Favorites row skeleton — signed-in users only. -->
    @if (userEmail !== undefined && userEmail !== 'Guest') {
      <div class="favorites-section tw-mb-[12px]" data-cy="replays-favorites-skeleton">
        <div class="replays-skeleton__bar tw-w-[24px] tw-h-[24px] tw-rounded-[4px] tw-mr-[19px] tw-float-left"></div>
        <div class="replays-skeleton__bar tw-w-[80px] tw-h-[22px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-float-left"></div>
        <div class="replays-skeleton__bar tw-w-[25px] tw-h-[25px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-ml-[20px] tw-mr-[20px] tw-float-left"></div>
        <div class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left">
          @for (chip of [1,2,3,4]; track chip) {
            <div class="replays-skeleton__bar tw-w-[273px] tw-h-[30px] tw-rounded-[4px] tw-mr-[18px] tw-flex-shrink-0"></div>
          }
        </div>
        <div class="replays-skeleton__bar tw-w-[25px] tw-h-[25px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-ml-[20px] tw-mr-[20px] tw-float-left"></div>
      </div>
    }

    <!-- Tab pill placeholder -->
    <div class="tw-flex">
      <div class="replays-skeleton__bar tw-h-[42px] tw-w-[190px] tw-rounded-t-[4px]"></div>
    </div>

    <!-- Results panel skeleton — filter row + table stripes -->
    <div class="replays-skeleton__panel tw-mt-[8px] tw-rounded-[13px] tw-bg-[#E3ECF8] tw-p-[16px]">
      <!-- Results count placeholder -->
      <div class="replays-skeleton__bar tw-h-[14px] tw-w-[140px] tw-rounded-[4px] tw-mb-[16px]"></div>

      <!-- Filter row placeholders — date, today, breed, track, clear -->
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-[12px] tw-mb-[18px]">
        <div class="replays-skeleton__bar tw-h-[40px] tw-w-[190px] tw-rounded-[7px]"></div>
        <div class="replays-skeleton__bar tw-h-[40px] tw-w-[80px] tw-rounded-[7px]"></div>
        <div class="replays-skeleton__bar tw-h-[16px] tw-w-[40px] tw-rounded-[4px]"></div>
        <div class="replays-skeleton__bar tw-h-[40px] tw-w-[220px] tw-rounded-[20px]"></div>
        <div class="replays-skeleton__bar tw-h-[40px] tw-w-[220px] tw-rounded-[20px]"></div>
        <div class="replays-skeleton__bar tw-h-[34px] tw-w-[80px] tw-rounded-[20px] tw-ml-auto"></div>
      </div>

      <!-- Table skeleton — header + 6 stripe rows -->
      <div class="tw-bg-[#FFFFFF] tw-rounded-[6px] tw-p-[12px]">
        <div class="tw-flex tw-gap-[10px] tw-mb-[14px]">
          @for (col of [1,2,3,4,5,6,7]; track col) {
            <div class="replays-skeleton__bar tw-h-[16px] tw-flex-1 tw-rounded-[4px]"></div>
          }
        </div>
        @for (row of [1,2,3,4,5,6]; track row) {
          <div class="tw-flex tw-gap-[10px] tw-mb-[12px]">
            @for (col of [1,2,3,4,5,6,7]; track col) {
              <div class="replays-skeleton__bar tw-h-[14px] tw-flex-1 tw-rounded-[4px]"></div>
            }
          </div>
        }
      </div>
    </div>
  </section>
</ng-template>

<!-- Favorites row — shared so it shows in content and above the profile panel -->
<ng-template #favoritesBar>
  @if (
    userEmail !== undefined
    && userEmail !== 'Guest'
  ) {
    <div class="favorites-section tw-mb-[12px]" data-cy="replays-favorites">
      <img
        src="/assets/images/wager/favorites_icon.svg"
        class="tw-inline tw-mr-[19px] tw-float-left"
        alt="favorites"
      >
      <span
        class="
          tw-text-[18px]
          tw-relative
          tw-top-[3px]
          tw-mt-[5px]
          tw-float-left
        "
      >
        {{ 'ui.wager.favorites' | translate: 'Favorites' }}
      </span>
      <mat-icon
        (click)="scrollTo('prev')"
        class="
          tw-text-[25px]
          tw-text-[#B5C5D8]
          tw-relative
          tw-cursor-pointer
          tw-top-[3px]
          tw-mt-[5px]
          tw-ml-[20px]
          tw-mr-[20px]
          tw-float-left
        "
        data-cy="replays-favorites-prev"
      >
        arrow_back
      </mat-icon>
      <div
        #favorites
        class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left tw-overflow-hidden"
      >
        <ul class="tw-text-center tw-flex">
          <li
            *ngFor="
              let favorite of (listOfFavoriteEvents | sortEvents: sortBy: eventTypeFilter: setListFavorites);
              trackBy: trackFavoriteFn
            "
            (click)="selectFavorite(favorite)"
            class="
              tw-text-[#FFFFFF]
              tw-w-[273px]
              tw-h-[30px]
              tw-content-center
              tw-inline-block
              tw-rounded-[4px]
              tw-bg-[#557DAC]
              tw-mr-[18px]
              tw-cursor-pointer
            "
            [ngClass]="{'active-event' : selectedEvent?.id === favorite.id}"
            [attr.data-cy]="'replays-favorite-' + favorite.id"
          >
            <!-- Mirror #eventSelection: track (country code), race, status/MTP -->
            <span data-cy="favorite-event-name">
              {{
                favorite?.CountryCode
                  ? favorite?.CountryCode === 'US' || favorite?.CountryCode === 'CA'
                    ? favorite?.ToteResponse?.EventDetail?.EventName
                    : favorite?.CountryCode + '-' + favorite?.ToteResponse?.EventDetail?.EventName
                  : favorite?.ToteResponse?.EventDetail?.EventName
              }}
            </span>

            <span data-cy="favorite-current-race">
              {{
                favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId
                  ? '('+(favorite?.ToteResponse?.EventDetail?.TrackType | getTrackType).charAt(0)
                    + ' R'+favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId+')'
                  : ''
              }}
            </span>

            <span data-cy="favorite-event-status">
              {{
                favorite?.ToteResponse?.EventDetail?.currentRace
                  ? (
                      (favorite?.ToteResponse?.EventDetail?.currentRace)?.RaceStatus
                        | getRaceStatus: (
                          favorite?.ToteResponse?.EventDetail?.currentRace
                        )?.PostTime: favorite?.eventName
                        | countdown: false: favorite?.eventName | async
                    )
                  : (favorite?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)
              }}
            </span>
          </li>
        </ul>
      </div>
      <mat-icon
        (click)="scrollTo('next')"
        class="
          tw-text-[25px]
          tw-text-[#B5C5D8]
          tw-relative
          tw-cursor-pointer
          tw-top-[3px]
          tw-mt-[5px]
          tw-ml-[20px]
          tw-mr-[20px]
          tw-float-left
        "
        data-cy="replays-favorites-next"
      >
        arrow_forward
      </mat-icon>
    </div>
  }
</ng-template>
